import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 8: Breath as Bridge
 * Large translucent sphere with breathing animation
 * 4-7-8 breath rhythm when no audio
 * Particle emission on exhale
 */
export default function BreathingSphere({ audioData, isActive, position = [0, 0, 0] }) {
  const sphereRef = useRef()
  const glowRef = useRef()
  const particlesRef = useRef()

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  // Particle system for exhale
  const particleCount = 200
  const particles = useMemo(() => {
    const positions = new Float32Array(particleCount * 3)
    const velocities = new Float32Array(particleCount * 3)

    for (let i = 0; i < particleCount * 3; i += 3) {
      // Start at sphere surface
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      const radius = 2.2

      positions[i] = radius * Math.sin(phi) * Math.cos(theta)
      positions[i + 1] = radius * Math.sin(phi) * Math.sin(theta)
      positions[i + 2] = radius * Math.cos(phi)

      // Velocity outward
      const speed = 0.02 + Math.random() * 0.03
      velocities[i] = positions[i] * speed
      velocities[i + 1] = positions[i + 1] * speed
      velocities[i + 2] = positions[i + 2] * speed
    }

    return { positions, velocities }
  }, [])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    // 4-7-8 breathing cycle (19 seconds total)
    const cycleTime = time % 19
    let breathPhase = 0
    let isExhaling = false

    if (cycleTime < 4) {
      // Inhale (4 seconds)
      breathPhase = cycleTime / 4
    } else if (cycleTime < 11) {
      // Hold (7 seconds)
      breathPhase = 1
    } else {
      // Exhale (8 seconds)
      breathPhase = 1 - ((cycleTime - 11) / 8)
      isExhaling = true
    }

    // Use audio amplitude to modulate breathing if present
    const audioModulation = amplitude > 0.1 ? amplitude : 0
    const finalPhase = breathPhase + audioModulation * 0.2

    if (sphereRef.current) {
      // Breathing scale: 2.0 (exhaled) to 2.5 (inhaled)
      const scale = 2.0 + finalPhase * 0.5
      sphereRef.current.scale.setScalar(scale)

      // Gentle rotation
      sphereRef.current.rotation.y += delta * 0.1
      sphereRef.current.rotation.x = Math.sin(time * 0.2) * 0.1

      // Opacity pulse with breathing
      sphereRef.current.material.opacity = 0.3 + finalPhase * 0.2
    }

    if (glowRef.current) {
      // Outer glow responds to breath
      const glowScale = 2.3 + finalPhase * 0.6
      glowRef.current.scale.setScalar(glowScale)
      glowRef.current.material.opacity = 0.15 + finalPhase * 0.1
    }

    // Particle emission during exhale
    if (particlesRef.current && isExhaling) {
      const positions = particlesRef.current.geometry.attributes.position.array

      for (let i = 0; i < particleCount * 3; i += 3) {
        // Move particles outward
        positions[i] += particles.velocities[i]
        positions[i + 1] += particles.velocities[i + 1]
        positions[i + 2] += particles.velocities[i + 2]

        // Reset particles that go too far
        const distance = Math.sqrt(
          positions[i] ** 2 +
          positions[i + 1] ** 2 +
          positions[i + 2] ** 2
        )

        if (distance > 5) {
          // Reset to sphere surface
          const theta = Math.random() * Math.PI * 2
          const phi = Math.acos(2 * Math.random() - 1)
          const radius = 2.2 + finalPhase * 0.5

          positions[i] = radius * Math.sin(phi) * Math.cos(theta)
          positions[i + 1] = radius * Math.sin(phi) * Math.sin(theta)
          positions[i + 2] = radius * Math.cos(phi)
        }
      }

      particlesRef.current.geometry.attributes.position.needsUpdate = true
    }
  })

  return (
    <group position={position}>
      {/* Main breathing sphere */}
      <mesh ref={sphereRef}>
        <sphereGeometry args={[2, 64, 64]} />
        <meshPhongMaterial
          color="#00FF88"
          transparent
          opacity={0.3}
          emissive="#00FF88"
          emissiveIntensity={0.4}
          shininess={100}
        />
      </mesh>

      {/* Outer glow sphere */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[2.3, 32, 32]} />
        <meshBasicMaterial
          color="#88FFAA"
          transparent
          opacity={0.15}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Particle system */}
      <points ref={particlesRef}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={particleCount}
            array={particles.positions}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.05}
          color="#AAFFCC"
          transparent
          opacity={0.6}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
        />
      </points>

      {/* Central light */}
      <pointLight position={[0, 0, 0]} intensity={1.5} color="#00FF88" distance={8} />
    </group>
  )
}
